import { ApplicationInsights, SeverityLevel, ITelemetryItem, IEventTelemetry, ITraceTelemetry, IExceptionTelemetry } from '@microsoft/applicationinsights-web';
import { TelemetryContract } from '@/contracts/TelemetryContract';

class TelemetryService implements TelemetryContract {
	private readonly appInsights: ApplicationInsights;

	constructor() {
		const instrumentationKey = process.env.VUE_APP_INSIGHTS_INSTRUMENTATION_KEY;
    
		if (!instrumentationKey) {
			console.warn('[TelemetryService] No instrumentation key found. Telemetry disabled.');
		}
		
		this.appInsights = new ApplicationInsights({
			config: {
				instrumentationKey: instrumentationKey || '',
				enableAutoRouteTracking: true,
				disableTelemetry: !instrumentationKey,
				enableCorsCorrelation: true,
				correlationHeaderDomains: [
					process.env.VUE_APP_API_DOMAIN,
				].filter(Boolean) as string[],
				correlationHeaderExcludePatterns: [
					/.*\.googleapis\.com/,
					/.*\.google\.com/,
				],
				distributedTracingMode: 2, // W3C traceparent — backend (commercialapps.mutualofenumclaw.net) supports W3C
				enableRequestHeaderTracking: true,
				enableResponseHeaderTracking: true,
				disableFetchTracking: false,
				disableAjaxTracking: false,
				enableAjaxPerfTracking: true,
				enableAjaxErrorStatusText: true,
				maxBatchInterval: 5000,
				disableExceptionTracking: false,
				loggingLevelTelemetry: process.env.NODE_ENV === 'development' ? 2 : 0,
			},
		});
		this.appInsights.loadAppInsights();

		this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
			if (!envelope.tags) envelope.tags = {};
			
			// Identifies this specific application component in the App Map
            envelope.tags["ai.cloud.role"] = "fact-commercial-web";
            
            // Identifies the environment (since 'server instance' doesn't apply to client-side JS)
            envelope.tags["ai.cloud.roleInstance"] = process.env.NODE_ENV || "development";
		});
	}

	trackEvent(event: IEventTelemetry) {
		this.appInsights.trackEvent(event);
	}

	trackTrace(trace: ITraceTelemetry) {
		this.appInsights.trackTrace(trace);
	}

	trackException(
		error: Error | IExceptionTelemetry | unknown, // OPTIMIZATION: Widen type to handle unknown throws
		severityLevel: SeverityLevel = SeverityLevel.Error,
		properties?: Record<string, any>,
		measurements?: Record<string, number>
	): void {
		if (error instanceof Error) {
			this.appInsights.trackException({
				exception: error,
				severityLevel,
				properties,
				measurements
			});
		} else if (typeof error === 'object' && error !== null && 'exception' in error) {
			// Handle IExceptionTelemetry interface
			const telemetry = error as IExceptionTelemetry;
			this.appInsights.trackException({
				...telemetry,
				properties: { ...telemetry.properties, ...properties },
				measurements: { ...telemetry.measurements, ...measurements }
			});
		} else {
			// Handle non-standard errors (strings, etc)
			this.appInsights.trackException({
				exception: new Error(String(error)),
				severityLevel,
				properties: { ...properties, originalError: error },
				measurements
			});
		}
	}

	trackPageView(name?: string, uri?: string) {
		this.appInsights.trackPageView({ name, uri });
	}

	startTrackEvent(name: string): void {
		this.appInsights.startTrackEvent(name);
	}

	stopTrackEvent(name: string, properties?: Record<string, any>, measurements?: Record<string, number>) {
		this.appInsights.stopTrackEvent(name, properties, measurements);
	}

	trackMetric(name: string, average: number, properties?: Record<string, any>) {
		this.appInsights.trackMetric({ name, average }, properties);
	}

	flush() {
		this.appInsights.flush();
	}

	get severityLevel() {
		return SeverityLevel;
	}
}

const telemetryService = new TelemetryService();
export default telemetryService;
